// Grok, OpenCode and Antigravity left 2026-10-17 at the principal's word: no grant is minted for them.
export const MCP_HARNESSES = Object.freeze(["claude", "codex", "aiSdk", "cursor"])

const MCP_URL = /^https:\/\/[a-z0-9.-]+\.convex\.site\/mcp$/

function convexErrorMessage(body) {
  if (typeof body?.errorMessage === "string" && body.errorMessage.length > 0) return body.errorMessage
  if (typeof body?.errorData === "string" && body.errorData.length > 0) return body.errorData
  return null
}

function parseGrant(grant) {
  if (!grant || typeof grant !== "object") return null
  if (
    typeof grant.grantId !== "string" ||
    typeof grant.mcpUrl !== "string" ||
    typeof grant.token !== "string" ||
    typeof grant.expiresAt !== "number" ||
    !Array.isArray(grant.tools) ||
    typeof grant.worldKey !== "string" ||
    // The mint's authoritative Actor identity must survive the handoff: the
    // Desk's deposit parser refuses Actor-less grants (loginHttp
    // parseOneGrant), so dropping this field here turns every sign-in into
    // invalid_callback.
    typeof grant.actorId !== "string" ||
    grant.actorId.trim().length === 0 ||
    !MCP_HARNESSES.includes(grant.harness) ||
    typeof grant.name !== "string" ||
    grant.name.trim().length === 0
  ) {
    return null
  }
  if (!MCP_URL.test(grant.mcpUrl)) return null
  return {
    grantId: grant.grantId,
    mcpUrl: grant.mcpUrl,
    token: grant.token,
    expiresAt: grant.expiresAt,
    tools: grant.tools.filter((tool) => typeof tool === "string"),
    worldKey: grant.worldKey,
    actorId: grant.actorId.trim(),
    harness: grant.harness,
    name: grant.name.trim(),
  }
}

export function parseConvexBatchMintResult(body) {
  if (!body || body.status !== "success" || !Array.isArray(body.value)) {
    return { ok: false, error: convexErrorMessage(body) ?? "Convex mint refused" }
  }
  const grants = []
  const seen = new Set()
  for (const value of body.value) {
    const grant = parseGrant(value)
    const grantKey = grant ? `${grant.harness}:${grant.worldKey}` : null
    if (!grant || seen.has(grantKey)) {
      return { ok: false, error: "Convex batch mint returned an unusable grant array" }
    }
    seen.add(grantKey)
    grants.push(grant)
  }
  return { ok: true, grants }
}

async function convexHttp({ convexUrl, clerkConvexJwt, kind, path, args }) {
  if (typeof convexUrl !== "string" || !convexUrl.startsWith("https://")) {
    return { ok: false, error: "Production Convex is required to mint the MCP token" }
  }
  if (typeof clerkConvexJwt !== "string" || clerkConvexJwt.length === 0) {
    return { ok: false, error: "Product Clerk did not issue a Convex identity token" }
  }
  const response = await fetch(`${convexUrl.replace(/\/$/, "")}/api/${kind}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${clerkConvexJwt}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ path, format: "json", args }),
  })
  if (!response.ok) {
    return { ok: false, error: kind === "query" ? "Convex World list HTTP refused" : "Convex mint HTTP refused" }
  }
  return { ok: true, body: await response.json() }
}

export async function mintEntitledWorldGrantsOnHandoff({ convexUrl, clerkConvexJwt, requestedHarnesses, renewable }) {
  // Older installed clients cannot deposit newer harness names.
  const harnesses = requestedHarnesses === undefined
    ? ["claude", "codex", "aiSdk"]
    : requestedHarnesses.split(",")
  if (harnesses.length === 0 || new Set(harnesses).size !== harnesses.length ||
      harnesses.some(harness => !MCP_HARNESSES.includes(harness))) {
    return { ok: false, error: "The app requested unsupported conversation providers" }
  }
  const http = await convexHttp({
    convexUrl,
    clerkConvexJwt,
    kind: "mutation",
    path: "sessionGrants:mintMembershipGrants",
    // W-2308dc50: renewable grants only for a Desk that declared it renews
    // them and keeps their bearers to revoke; an older Desk's request is unchanged.
    args: renewable === true ? { harnesses, renewable: true } : { harnesses },
  })
  if (!http.ok) return http
  const parsed = parseConvexBatchMintResult(http.body)
  if (!parsed.ok) return parsed
  // A successful sign-in must not silently omit a requested engine.
  const worldKeys = new Set(parsed.grants.map((grant) => grant.worldKey))
  if (worldKeys.size === 0 || [...worldKeys].some((worldKey) =>
    harnesses.some((harness) => !parsed.grants.some((grant) =>
      grant.worldKey === worldKey && grant.harness === harness)))) {
    return { ok: false, error: "Convex mint omitted a requested conversation provider; please retry sign-in" }
  }
  return parsed
}

// P0, 7 Oct 2026: the Desk keeps none of a grant's tool names. It checks only that the list is not empty and stores
// its own (loginHttp.ts parseOneGrant: tools: MEMBERSHIP_GRANT_TOOLS), because the World decides a member's tools live.
// Posting every World's full list, once per engine harness, made a many-World account's deposit pass the Desk's 1 MiB
// cap (1,097,096 bytes, refused 413), so the form carries each grant's first non-empty tool name only (the Desk drops
// empty names, so an empty first name would leave it nothing). What the grant authorizes is the World's record and is
// unchanged. Only for a Desk that declared it renews (`renewable`, grant_renewal=1, 0.0.171 on): a Desk before 0.0.138
// stored the posted list as the grant's tools, and one name there would refuse every other tool.
export function mcpDepositFields({ state, identity, grants, renewable }) {
  const posted = renewable === true
    ? grants.map((grant) => ({ ...grant, tools: grant.tools.filter((tool) => tool.length > 0).slice(0, 1) }))
    : grants
  return {
    state,
    identity,
    grants: JSON.stringify(posted),
  }
}
